/**
 * TermTooltip — Glossaire métier accessible depuis n'importe où.
 * Utilisé pour expliquer les termes immobiliers (Mandat exclusif, DVF, Compromis…)
 * à des utilisateurs non experts.
 *
 * Usage :
 *   <TermTooltip term="mandat_exclusif">
 *     Mandat exclusif
 *   </TermTooltip>
 */
import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

export const GLOSSARY = {
    mandat_exclusif: {
        label: 'Mandat exclusif',
        definition: "Contrat où vous êtes le SEUL agent autorisé à vendre le bien. C'est le mandat qui rapporte le plus (3× plus en CA qu'un simple) car le vendeur s'engage avec vous uniquement.",
    },
    mandat_simple: {
        label: 'Mandat simple',
        definition: "Le vendeur peut confier son bien à plusieurs agences en même temps. Plus facile à signer mais beaucoup moins rentable car la concurrence dilue vos efforts.",
    },
    mandat_semi_exclusif: {
        label: 'Mandat semi-exclusif',
        definition: "Le vendeur peut chercher lui-même un acheteur (sans agence), mais s'il passe par une agence, ce sera vous. Compromis entre simple et exclusif.",
    },
    dvf: {
        label: 'DVF',
        definition: "Demandes de Valeurs Foncières — base officielle gouvernementale qui recense toutes les ventes immobilières réalisées en France. Sert à comparer le prix d'un bien aux ventes récentes du quartier.",
    },
    compromis: {
        label: 'Compromis de vente',
        definition: "Avant-contrat signé entre vendeur et acquéreur qui engage les deux parties (sous conditions suspensives, ex: prêt). Étape juste avant l'acte authentique chez le notaire.",
    },
    acte_authentique: {
        label: 'Acte authentique',
        definition: "Signature finale chez le notaire qui transfère officiellement la propriété du bien et déclenche votre commission. Environ 3 mois après le compromis.",
    },
    joignabilite: {
        label: 'Joignabilité',
        definition: "Pourcentage de tentatives d'appel qui aboutissent à une vraie conversation (pas de répondeur). Indicateur clé : sous 70%, vous perdez beaucoup de leads.",
    },
    estimation: {
        label: 'Estimation',
        definition: "Évaluation de la valeur d'un bien que vous remettez à un vendeur potentiel. Étape clé pour gagner sa confiance et signer un mandat.",
    },
    pipeline: {
        label: 'Pipeline',
        definition: "Ensemble des leads et mandats en cours qui pourraient se transformer en vente. La valeur pondérée tient compte de la probabilité de signature selon le type de mandat.",
    },
    relance: {
        label: 'Relance',
        definition: "Action de recontacter un prospect/client après un premier échange. Sans relance, 80% des opportunités sont perdues. Règle d'or : J+3, J+7, J+14.",
    },
    homly_score: {
        label: 'HomlyScore',
        definition: "Score IA 0-100 prédisant la qualité d'un lead (probabilité qu'il signe un mandat avec vous). Calculé automatiquement à partir de la motivation, urgence, prix souhaité et contexte.",
    },
    silence: {
        label: 'Silence mandat',
        definition: "Nombre de jours sans aucune action enregistrée sur un mandat (appel, email, visite, relance). Au-delà de 14j, le risque de perdre le mandat augmente fortement.",
    },
};

export default function TermTooltip({ term, children, showIcon = false, className = '' }) {
    const entry = GLOSSARY[term];
    if (!entry) {
        // Fallback : just render children if term not found
        return <span className={className}>{children}</span>;
    }
    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className={`inline-flex items-center gap-1 underline decoration-dotted decoration-1 underline-offset-2 cursor-help ${className}`}
                        data-testid={`term-tooltip-${term}`}
                    >
                        {children || entry.label}
                        {showIcon && <HelpCircle className="w-3 h-3 opacity-50" />}
                    </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px] text-xs leading-relaxed">
                    <p className="font-bold text-foreground mb-1">{entry.label}</p>
                    <p className="text-muted-foreground">{entry.definition}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
